import * as React from 'react';

import {
  DropdownMenu,
  DropdownMenuContent,
  type DropdownMenuContentProps,
  DropdownMenuTrigger,
} from '@/ux-common/components/dropdown-menu/dropdown-menu';
import { Input } from '@/ux-common/components/input/input';
import { useInputGroup } from '@/ux-common/components/input/input.context';
import {
  InputTriggerField,
  type InputTriggerFieldProps,
} from '@/ux-common/components/input/input-trigger-field';
import { cn } from '@/ux-common/lib/utils';

/**
 * DropdownInput — a form field that opens a DropdownMenu: the Input's bordered
 * field drawn as a menu button, showing a value you write (or a placeholder) and a
 * chevron. Use it to choose several values from checkbox rows, or for any list a
 * Select cannot hold.
 *
 * It is an `Input` group, like Select: `InputLabel` and `InputHelper` wire
 * themselves to the trigger with no ids at the call site, and every root state
 * (`state="error"`, `.input-lg`, …) applies. The trigger and the menu are both named
 * after the label.
 *
 * For ONE value, use Select: it is a real combobox and listbox, with typeahead and a
 * form value. A menu holds no value — the app owns it, and writes the field's text.
 *
 * @example
 * ```tsx
 * <DropdownInput>
 *   <InputLabel>Event days</InputLabel>
 *   <DropdownInputTrigger placeholder="No days">{days.join(', ')}</DropdownInputTrigger>
 *   <DropdownInputContent>
 *     {DAYS.map((day) => (
 *       <DropdownMenuCheckboxItem
 *         key={day}
 *         checked={days.includes(day)}
 *         onCheckedChange={(on) => toggle(day, on)}
 *       >
 *         {day}
 *       </DropdownMenuCheckboxItem>
 *     ))}
 *   </DropdownInputContent>
 *   <InputHelper>Defrost runs on these days.</InputHelper>
 * </DropdownInput>
 * ```
 */
export interface DropdownInputProps extends React.ComponentPropsWithoutRef<typeof DropdownMenu> {
  /** Class(es) for the `.input` group that wraps the field. */
  className?: string;
  /**
   * The group's validation state, as on `Input` — applies `.input-{state}` and
   * reaches the group's slots.
   */
  state?: React.ComponentProps<typeof Input>['state'];
  /**
   * Refuses the field and says why — a view-only session, a missing permission.
   * The trigger stays focusable and hoverable, so a keyboard user reaches it and a
   * `withTooltip` on it still fires (a natively disabled button gets no pointer or
   * focus events). It reads and looks disabled (`aria-disabled`), never opens, and
   * announces the reason as its description without depending on hover. Wins over
   * the trigger's `disabled` when both are set.
   */
  disabledReason?: React.ReactNode;
  /**
   * The group's `data-component` marker — `"DropdownInput"` unless you name it. The
   * DropdownMenu root renders no element, so this is the field's outermost element:
   * a call site annotating its own component root sets it here.
   */
  'data-component'?: string;
}

/**
 * The root's refusal, for the trigger: the id of the visually hidden reason while
 * `disabledReason` is set.
 */
const DropdownInputRefusalContext = React.createContext<string | undefined>(undefined);

/**
 * DropdownInput — the DropdownMenu root inside an `.input.dropdown-input` group.
 * Root props (`open`, `defaultOpen`, `onOpenChange`, `modal`, `dir`) pass through;
 * `className` and `state` go to the group.
 *
 * A refused field (`disabledReason`) is held shut here, whatever opens it: the
 * menu is controlled closed and ignores open requests. The reason is rendered once,
 * visually hidden, for the trigger's `aria-describedby`.
 */
export function DropdownInput({
  className,
  state,
  children,
  disabledReason,
  'data-component': dataComponent = 'DropdownInput',
  ...props
}: DropdownInputProps) {
  const reasonId = React.useId();
  const refused = disabledReason != null && disabledReason !== false && disabledReason !== '';
  const rootProps = refused ? { ...props, open: false, onOpenChange: undefined } : props;

  return (
    <DropdownMenu {...rootProps}>
      <DropdownInputRefusalContext.Provider value={refused ? reasonId : undefined}>
        <Input
          data-component={dataComponent}
          state={state}
          className={cn('dropdown-input', className)}
        >
          {children}
          {refused && (
            <span id={reasonId} className="sr-only">
              {disabledReason}
            </span>
          )}
        </Input>
      </DropdownInputRefusalContext.Provider>
    </DropdownMenu>
  );
}

DropdownInput.displayName = 'DropdownInput';

export type DropdownInputTriggerProps = Omit<
  InputTriggerFieldProps,
  'refusalId' | 'valueClassName' | 'chevronClassName'
>;

/**
 * DropdownInputTrigger — the field. A `<button aria-haspopup="menu">` drawn as the
 * bordered `.input-field`, showing `children` (the value, as text you write) or
 * `placeholder`, and a trailing chevron that turns while the menu is open.
 *
 * It joins the enclosing group, so `InputLabel`'s `htmlFor` and every
 * `InputHelper`'s description reach it. Its name is the label AND the value
 * ("Event days Mon, Wed"), so a screen reader hears what is chosen without opening
 * the menu. `type` is pinned to `"button"`, so it never submits a form.
 *
 * For a leading icon, pass an `InputLeadingIcon` as a child: it renders before the
 * value, sized, coloured and spaced as on InputField, and decorative.
 *
 * For `state="new"`, pass an `InputValueChange` as a child: it renders after the
 * value, before the chevron, the value beside it hugs its text, and its "Previous
 * value …" joins the button's name.
 *
 * For an error or warning glyph, pass an `InputStateIcon`: it joins the trailing
 * cluster before the chevron. Not `InputStateInfo` — it is a button, and this is
 * already one; passed anyway, it is dropped with a dev warning.
 *
 * The field itself is the shared `InputTriggerField`, under `DropdownMenuTrigger
 * asChild`. A refused trigger runs none of the app's activation handlers, the rule
 * SelectTrigger and refused DropdownMenu items follow; hover and focus still reach
 * it, so a tooltip on it keeps working.
 */
export const DropdownInputTrigger = React.forwardRef<HTMLButtonElement, DropdownInputTriggerProps>(
  ({ className, id, disabled, ...props }, ref) => {
    const reasonId = React.useContext(DropdownInputRefusalContext);
    const refused = reasonId !== undefined;

    return (
      <DropdownMenuTrigger asChild disabled={refused ? false : disabled}>
        <InputTriggerField
          ref={ref}
          // Passed even when undefined, so Radix's own trigger id never replaces the
          // group's control id (the Slot lets the child's props win).
          id={id}
          disabled={disabled}
          refusalId={reasonId}
          data-component="DropdownInputTrigger"
          className={cn('dropdown-input-trigger', className)}
          valueClassName="dropdown-input-value"
          chevronClassName="dropdown-input-chevron"
          {...props}
        />
      </DropdownMenuTrigger>
    );
  }
);

DropdownInputTrigger.displayName = 'DropdownInputTrigger';

export type DropdownInputContentProps = DropdownMenuContentProps;

/**
 * DropdownInputContent — the menu. A DropdownMenuContent (portalled, 2px below the
 * field, start-aligned) at least 250px and at least as wide as the field, and
 * named after the group's label. Fill it with the DropdownMenu rows: `DropdownMenuCheckboxItem` for several
 * values (they keep the menu open), `DropdownMenuRadioGroup` / `…RadioItem`,
 * `DropdownMenuItem`, `…Label`, `…Separator`, `…Group`.
 */
export const DropdownInputContent = React.forwardRef<HTMLDivElement, DropdownInputContentProps>(
  ({ className, 'aria-labelledby': ariaLabelledBy, ...props }, ref) => {
    const group = useInputGroup();
    // Radix names the menu after the trigger, and the trigger's text is the value —
    // so without this the menu would be called "Mon, Wed" rather than "Event days".
    const labelledBy = ariaLabelledBy ?? group?.labelId ?? group?.controlId;

    return (
      <DropdownMenuContent
        ref={ref}
        aria-labelledby={labelledBy}
        data-component="DropdownInputContent"
        className={cn('dropdown-input-content', className)}
        {...props}
      />
    );
  }
);

DropdownInputContent.displayName = 'DropdownInputContent';
